import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { BagPack } from "@/components/BagPack";
import { useLang } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { formatPrice, isAvailable } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Only verified fields are rendered; anything missing is skipped (no empty rows).
const SPEC_KEYS = ["altitud", "clasificacion", "variedad", "proceso", "cosecha", "tueste", "lote"];

// Card background = a very soft wash of the product's own sticker colour.
const tint = (hex, amount = 0.16) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const mix = (c, base) => Math.round(c * amount + base * (1 - amount));
  const r = mix((n >> 16) & 255, 250);
  const g = mix((n >> 8) & 255, 247);
  const b = mix(n & 255, 240);
  return `rgb(${r}, ${g}, ${b})`;
};

const EncuentraCafe = () => {
  const { lang, t } = useLang();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch((e) => console.error("products error", e));
  }, []);

  // The card is one extended link; the cart button is an independent control.
  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAvailable(p)) return;
    addItem(p);
    toast.success(`${p.name} ${t.encuentra.added}`);
  };

  return (
    <section
      id="coffees"
      className="bg-[#F5F1E8] py-24 md:py-32 px-6 md:px-12 lg:px-20 scroll-mt-[104px]"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="text-[#3B2523]/70 uppercase tracking-[0.25em] text-xs font-semibold mb-5">
            {t.encuentra.tag}
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#3B2523]">
            {t.encuentra.title}
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#3B2523]/70 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
            {t.encuentra.intro}
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-14 items-stretch">
          {products.map((p, i) => {
            const specs = SPEC_KEYS.map((k) => [k, p.specs?.[k]]).filter(([, v]) => v && v[lang]);
            const available = isAvailable(p);
            const shortName = p.name.replace(/^Naguarà\s*·\s*/, "");
            const linkLabel = available
              ? `${t.encuentra.view} ${shortName}`
              : `${t.avail.view} ${shortName} ${t.avail.viewSuffix}`;
            return (
              <motion.article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="group relative bg-white border border-[#3B2523]/10 rounded-3xl overflow-hidden flex flex-col transition-[border-color,box-shadow] duration-300 hover:border-[#3B2523]/25 hover:shadow-[0_10px_28px_rgba(59,37,35,0.10)] focus-within:border-[#3B2523]/30 focus-within:ring-2 focus-within:ring-[#F4D35E] focus-within:ring-offset-2 focus-within:ring-offset-[#F5F1E8]"
              >
                {/* Extended link: covers the whole card so the pack, the name
                    and any empty space open the same coffee sheet. It sits
                    below the cart button (z-2), so that stays independent. */}
                <Link
                  to={`/cafe/${p.id}`}
                  aria-label={linkLabel}
                  data-testid={`product-card-link-${p.id}`}
                  className="absolute inset-0 z-[1] rounded-3xl outline-none"
                >
                  <span className="sr-only">{linkLabel}</span>
                </Link>

                {!available && (
                  <p
                    data-testid={`product-unavailable-badge-${p.id}`}
                    className="absolute top-4 left-4 z-[2] bg-[#8C2F1E] text-[#FFF6EC] text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 rounded-full shadow-lg"
                  >
                    {t.avail.badge}
                  </p>
                )}

                {/* Product image — the full pack, contained, never cropped */}
                <div
                  className="flex items-center justify-center px-6 pt-8 pb-4"
                  style={{ backgroundColor: tint(p.sticker || "#F1ECE0") }}
                >
                  <BagPack
                    product={p}
                    alt={`${t.producto.packAlt} ${p.name}, 250 g`}
                    testId={`product-image-${p.id}`}
                    shadow="drop-shadow-[0_14px_20px_rgba(59,37,35,0.28)]"
                    className="h-[230px] sm:h-[260px] lg:h-[300px]"
                  />
                </div>

                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3
                      data-testid={`product-name-${p.id}`}
                      className="font-display text-xl font-bold text-[#3B2523]"
                    >
                      {p.name}
                    </h3>
                    <span className="font-display text-lg font-bold text-[#3B2523] whitespace-nowrap">
                      {formatPrice(p.price, lang)}
                    </span>
                  </div>
                  <p className="text-[#3B2523]/70 text-sm mt-1">
                    {p.subtitle[lang]} · {p.weight}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {p.notes[lang].map((n, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5F1E8] text-[#3B2523] border border-[#3B2523]/10"
                      >
                        {n}
                      </span>
                    ))}
                  </div>

                  {/* Same data as the real sticker on the pack — hidden when there is none */}
                  {specs.length > 0 && (
                    <dl
                      data-testid={`product-specs-${p.id}`}
                      className="mt-5 grid grid-cols-[124px_1fr] gap-x-4 gap-y-2 text-xs border-t border-[#3B2523]/10 pt-5"
                    >
                      {specs.map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt className="uppercase tracking-[0.14em] font-semibold text-[#3B2523]/70">
                            {t.encuentra.specs[k]}
                          </dt>
                          <dd className="text-[#3B2523]/80">{v[lang]}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  )}

                  <p className="text-[#3B2523]/70 text-sm mt-5 leading-relaxed flex-1 line-clamp-4">
                    {p.description[lang]}
                  </p>

                  {available ? (
                    <button
                      type="button"
                      data-testid={`add-to-cart-${p.id}`}
                      onClick={(e) => handleAdd(e, p)}
                      className="relative z-[2] mt-6 min-h-[48px] bg-[#3B2523] text-[#F5F1E8] font-semibold py-3.5 rounded-full transition-colors duration-300 hover:bg-[#F4D35E] hover:text-[#3B2523] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3B2523] focus-visible:ring-offset-2"
                    >
                      {t.encuentra.add}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      data-testid={`add-to-cart-${p.id}`}
                      className="relative z-[2] mt-6 min-h-[48px] bg-[#E3DCCB] text-[#6B534E] font-semibold py-3.5 rounded-full border border-[#3B2523]/15 cursor-not-allowed"
                    >
                      {t.avail.button}
                    </button>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EncuentraCafe;
